import { useEffect, useState } from 'react';
import { 
  Zap, Plus, Search, Trash2, Edit, Clock, 
  Calendar, Eye, EyeOff, Image, Package
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminFlashSales = () => {
  const [flashSales, setFlashSales] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [searchProduct, setSearchProduct] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    banner_image: '',
    start_time: '',
    end_time: '',
    discount_percentage: 20,
    products: [],
    is_active: true,
    show_on_homepage: true,
    priority: 0
  });

  useEffect(() => {
    loadFlashSales();
    loadProducts();
  }, []);

  const loadFlashSales = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/flash-sales`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFlashSales(response.data.flash_sales || []);
    } catch (error) {
      console.error('Error loading flash sales:', error);
      toast.error('Failed to load flash sales');
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data.products || []);
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.start_time || !formData.end_time) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.products.length === 0) {
      toast.error('Please add at least one product to the flash sale');
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const payload = {
        ...formData,
        start_time: new Date(formData.start_time).toISOString(),
        end_time: new Date(formData.end_time).toISOString()
      };

      if (editingSale) {
        await axios.put(`${API}/admin/flash-sales/${editingSale.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Flash sale updated successfully');
      } else {
        await axios.post(`${API}/admin/flash-sales`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Flash sale created successfully');
      }

      setShowModal(false);
      resetForm();
      loadFlashSales();
    } catch (error) {
      console.error('Error saving flash sale:', error);
      toast.error(error.response?.data?.detail || 'Failed to save flash sale');
    }
  };

  const handleDelete = async (saleId) => {
    if (!window.confirm('Are you sure you want to delete this flash sale?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/flash-sales/${saleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Flash sale deleted');
      loadFlashSales();
    } catch (error) {
      console.error('Error deleting flash sale:', error);
      toast.error('Failed to delete flash sale');
    }
  };

  const handleEdit = (sale) => {
    setEditingSale(sale);
    setFormData({
      title: sale.title,
      description: sale.description || '',
      banner_image: sale.banner_image || '',
      start_time: sale.start_time ? new Date(sale.start_time).toISOString().slice(0, 16) : '',
      end_time: sale.end_time ? new Date(sale.end_time).toISOString().slice(0, 16) : '',
      discount_percentage: sale.discount_percentage || 20,
      products: sale.products || [],
      is_active: sale.is_active,
      show_on_homepage: sale.show_on_homepage,
      priority: sale.priority || 0
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingSale(null);
    setFormData({
      title: '',
      description: '',
      banner_image: '',
      start_time: '',
      end_time: '',
      discount_percentage: 20,
      products: [],
      is_active: true,
      show_on_homepage: true,
      priority: 0
    });
    setSearchProduct('');
  };

  const addProductToSale = (product) => {
    if (formData.products.find(p => p.product_id === product.id)) {
      toast.error('Product already added');
      return;
    }

    const flashPrice = product.discount_price || product.price;
    const discountedPrice = Math.round(flashPrice * (1 - formData.discount_percentage / 100));

    setFormData({
      ...formData,
      products: [
        ...formData.products,
        {
          product_id: product.id,
          flash_price: discountedPrice,
          flash_stock: 10,
          sold_count: 0,
          product_name: product.name,
          original_price: flashPrice,
          product_image: product.images?.[0]
        }
      ]
    });
    setSearchProduct('');
  };

  const removeProductFromSale = (productId) => {
    setFormData({
      ...formData,
      products: formData.products.filter(p => p.product_id !== productId)
    });
  };

  const updateProductInSale = (productId, field, value) => {
    setFormData({
      ...formData,
      products: formData.products.map(p =>
        p.product_id === productId ? { ...p, [field]: value } : p
      )
    });
  };

  const getSaleStatus = (sale) => {
    const now = new Date();
    const startTime = new Date(sale.start_time);
    const endTime = new Date(sale.end_time);

    if (!sale.is_active) return { label: 'Inactive', color: 'bg-gray-100 text-gray-700' };
    if (now < startTime) return { label: 'Upcoming', color: 'bg-blue-100 text-blue-700' };
    if (now > endTime) return { label: 'Ended', color: 'bg-red-100 text-red-700' };
    return { label: 'Live Now', color: 'bg-green-100 text-green-700' };
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeRemaining = (endTime) => {
    const now = new Date();
    const end = new Date(endTime);
    const diff = end - now;
    
    if (diff <= 0) return 'Ended';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h`;
    }
    return `${hours}h ${minutes}m`;
  };

  const filteredProducts = products.filter(p => 
    p.name?.toLowerCase().includes(searchProduct.toLowerCase()) &&
    !formData.products.find(fp => fp.product_id === p.id)
  ).slice(0, 10);

  return (
    <div className="space-y-6" data-testid="admin-flash-sales">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-light text-charcoal flex items-center gap-2">
            <Zap className="w-6 h-6 text-orange-500" />
            Flash Sales
          </h1>
          <p className="text-charcoal-light mt-1">Create time-limited sales with countdown timers</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-gold text-white hover:bg-gold-dark transition-colors"
          data-testid="create-flash-sale-btn"
        >
          <Plus className="w-4 h-4" /> Create Flash Sale
        </button>
      </div>

      {/* Flash Sales List */}
      {loading ? (
        <div className="flex items-center justify-center min-h-[300px]">
          <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
        </div>
      ) : flashSales.length === 0 ? (
        <div className="bg-white border border-charcoal/10 p-12 text-center">
          <Zap className="w-12 h-12 mx-auto text-charcoal-light mb-3" />
          <p className="text-charcoal-light">No flash sales created yet</p>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="mt-4 text-gold hover:underline"
          >
            Create your first flash sale
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {flashSales.map((sale) => {
            const status = getSaleStatus(sale);
            
            return (
              <div 
                key={sale.id}
                className="bg-white border border-charcoal/10 overflow-hidden hover:border-gold/30 transition-colors"
              >
                <div className="flex flex-col md:flex-row">
                  {/* Banner Preview */}
                  <div className="md:w-48 h-32 md:h-auto bg-gradient-to-br from-orange-500 to-red-500 relative overflow-hidden flex-shrink-0">
                    {sale.banner_image ? (
                      <img src={sale.banner_image} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Zap className="w-12 h-12 text-white/50" />
                      </div>
                    )}
                    <span className={`absolute top-2 left-2 px-2 py-0.5 text-xs ${status.color}`}>
                      {status.label}
                    </span>
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-heading text-lg text-charcoal">{sale.title}</h3>
                        <p className="text-sm text-charcoal-light">{sale.description || 'No description'}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {sale.show_on_homepage ? (
                          <Eye className="w-4 h-4 text-green-500" title="Visible on homepage" />
                        ) : (
                          <EyeOff className="w-4 h-4 text-charcoal-light" title="Hidden from homepage" />
                        )}
                        <button
                          onClick={() => handleEdit(sale)}
                          className="p-2 hover:bg-beige transition-colors"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4 text-charcoal-light" />
                        </button>
                        <button
                          onClick={() => handleDelete(sale.id)}
                          className="p-2 hover:bg-red-50 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-4 text-sm mb-3">
                      <span className="flex items-center gap-1 text-charcoal-light">
                        <Calendar className="w-4 h-4" />
                        {formatDateTime(sale.start_time)}
                      </span>
                      <span className="text-charcoal-light">→</span>
                      <span className="flex items-center gap-1 text-charcoal-light">
                        {formatDateTime(sale.end_time)}
                      </span>
                      {status.label === 'Live Now' && (
                        <span className="flex items-center gap-1 text-orange-600 font-medium">
                          <Clock className="w-4 h-4 animate-pulse" />
                          {getTimeRemaining(sale.end_time)} left
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <span className="bg-orange-100 text-orange-700 px-2 py-0.5 text-sm">
                        {sale.discount_percentage}% OFF
                      </span>
                      <span className="text-sm text-charcoal-light">
                        <Package className="w-4 h-4 inline mr-1" />
                        {sale.products?.length || 0} products
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-ivory w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-charcoal/10">
              <h2 className="text-xl font-heading text-charcoal">
                {editingSale ? 'Edit Flash Sale' : 'Create Flash Sale'}
              </h2>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-charcoal mb-2">Sale Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Weekend Flash Sale"
                    className="w-full px-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                    required
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-charcoal mb-2">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={2}
                    placeholder="Limited time deals on selected items..."
                    className="w-full px-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-charcoal mb-2">Banner Image URL</label>
                  <input
                    type="url"
                    value={formData.banner_image}
                    onChange={(e) => setFormData({ ...formData, banner_image: e.target.value })}
                    placeholder="https://..."
                    className="w-full px-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                  />
                </div>
              </div>

              {/* Timing */}
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">Start Time *</label>
                  <input
                    type="datetime-local"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    className="w-full px-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">End Time *</label>
                  <input
                    type="datetime-local"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    className="w-full px-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">Default Discount %</label>
                  <input
                    type="number"
                    value={formData.discount_percentage}
                    onChange={(e) => setFormData({ ...formData, discount_percentage: parseFloat(e.target.value) || 0 })}
                    min="1"
                    max="90"
                    className="w-full px-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                  />
                </div>
              </div>

              {/* Products */}
              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Flash Sale Products *</label>
                
                {/* Product Search */}
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-charcoal-light" />
                  <input
                    type="text"
                    value={searchProduct}
                    onChange={(e) => setSearchProduct(e.target.value)}
                    placeholder="Search products to add..."
                    className="w-full pl-10 pr-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                  />
                  
                  {searchProduct && filteredProducts.length > 0 && (
                    <div className="absolute top-full left-0 right-0 bg-white border border-charcoal/20 shadow-lg z-10 max-h-60 overflow-y-auto">
                      {filteredProducts.map((product) => (
                        <button
                          key={product.id}
                          type="button"
                          onClick={() => addProductToSale(product)}
                          className="w-full flex items-center gap-3 p-3 hover:bg-beige text-left"
                        >
                          <img
                            src={product.images?.[0] || ''}
                            alt=""
                            className="w-10 h-10 object-cover bg-beige"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-charcoal truncate">{product.name}</p>
                            <p className="text-xs text-charcoal-light">
                              ₹{product.discount_price || product.price}
                            </p>
                          </div>
                          <Plus className="w-4 h-4 text-gold" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Selected Products */}
                {formData.products.length > 0 ? (
                  <div className="border border-charcoal/10 divide-y divide-charcoal/10">
                    {formData.products.map((fp) => (
                      <div key={fp.product_id} className="p-3 flex items-center gap-4">
                        <img
                          src={fp.product_image || ''}
                          alt=""
                          className="w-12 h-12 object-cover bg-beige flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-charcoal truncate">{fp.product_name}</p>
                          <p className="text-xs text-charcoal-light line-through">₹{fp.original_price}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <div>
                            <label className="text-xs text-charcoal-light block">Flash Price</label>
                            <input
                              type="number"
                              value={fp.flash_price}
                              onChange={(e) => updateProductInSale(fp.product_id, 'flash_price', parseInt(e.target.value) || 0)}
                              className="w-20 px-2 py-1 border border-charcoal/20 text-sm"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-charcoal-light block">Stock</label>
                            <input
                              type="number"
                              value={fp.flash_stock}
                              onChange={(e) => updateProductInSale(fp.product_id, 'flash_stock', parseInt(e.target.value) || 0)}
                              className="w-16 px-2 py-1 border border-charcoal/20 text-sm"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => removeProductFromSale(fp.product_id)}
                            className="p-1 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="border border-dashed border-charcoal/20 p-6 text-center text-charcoal-light">
                    Search and add products above
                  </div>
                )}
              </div>

              {/* Options */}
              <div className="flex flex-wrap gap-6">
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-5 h-5 rounded border-charcoal/20 text-gold focus:ring-gold"
                  />
                  <span className="text-sm text-charcoal">Active</span>
                </label>
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={formData.show_on_homepage}
                    onChange={(e) => setFormData({ ...formData, show_on_homepage: e.target.checked })}
                    className="w-5 h-5 rounded border-charcoal/20 text-gold focus:ring-gold"
                  />
                  <span className="text-sm text-charcoal">Show on Homepage</span>
                </label>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-charcoal">Priority:</label>
                  <input
                    type="number"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                    className="w-16 px-2 py-1 border border-charcoal/20 text-sm"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-4 pt-4 border-t border-charcoal/10">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="flex-1 px-4 py-2 border border-charcoal/20 text-charcoal hover:bg-beige"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-gold text-white hover:bg-gold-dark"
                >
                  {editingSale ? 'Update Flash Sale' : 'Create Flash Sale'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminFlashSales;
